/* Client widget: request to add/modify a secondary WhatsApp number.
   Visible in the client dashboard. Shows current secondary number (if any),
   pending request status, and the form to submit a new request. */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { MessageCircle, Send, Clock, CheckCircle, XCircle, RefreshCw } from "lucide-react";

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return "—"; }
};

const statusPill = (s) => {
  const m = {
    pending: { bg: "#FEF3C7", color: "#92400E", Icon: Clock, label: "En attente d'approbation" },
    approved: { bg: "#DCFCE7", color: "#166534", Icon: CheckCircle, label: "Approuvée" },
    rejected: { bg: "#FEE2E2", color: "#991B1B", Icon: XCircle, label: "Refusée" },
  }[s] || { bg: "#E2E8F0", color: "#334155", Icon: Clock, label: s };
  const I = m.Icon;
  return (
    <span style={{ background: m.bg, color: m.color, padding: "0.15rem 0.5rem", borderRadius: 999, fontSize: "0.7rem", fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
      <I size={11} /> {m.label}
    </span>
  );
};

export default function ClientWhatsAppSecondary({ apiCall }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [number, setNumber] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiCall("get", "/client/whatsapp-secondary");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const digits = number.replace(/\D/g, "");
    if (digits.length < 8) { toast.error("Numéro invalide (8 chiffres min.)"); return; }
    setSubmitting(true);
    try {
      await apiCall("post", "/client/whatsapp-secondary/request", { requested_number: digits, reason: reason.trim() || undefined });
      toast.success("Demande envoyée — l'admin va l'examiner");
      setNumber(""); setReason("");
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'envoi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="client-wa2-widget" style={{ background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: "1rem 1.1rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <MessageCircle size={18} color="#16A34A" />
          <strong style={{ fontSize: "0.95rem", color: "#0F172A" }}>Numéro WhatsApp secondaire</strong>
        </div>
        <button
          onClick={fetchStatus}
          className="btn"
          style={{ background: "transparent", color: "#475569", padding: "0.2rem 0.4rem" }}
          title="Actualiser"
        ><RefreshCw size={13} /></button>
      </div>

      <p style={{ fontSize: "0.78rem", color: "#64748B", marginBottom: 10 }}>
        Si votre numéro principal n'est pas joignable sur WhatsApp, ajoutez un numéro secondaire pour que l'administrateur et votre affilié puissent vous contacter.
      </p>

      {loading && <div style={{ color: "#64748B", fontSize: "0.85rem" }}>Chargement…</div>}

      {!loading && data && (
        <>
          {data.current_secondary ? (
            <div data-testid="client-wa2-current" style={{ background: "#DCFCE7", border: "1px solid #86EFAC", color: "#166534", borderRadius: 8, padding: "0.6rem 0.8rem", marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
              <CheckCircle size={16} />
              <div style={{ fontSize: "0.85rem" }}>
                Numéro actif : <strong>{data.current_secondary}</strong>
              </div>
            </div>
          ) : (
            <div style={{ background: "#F8FAFC", border: "1px dashed #CBD5E1", color: "#64748B", borderRadius: 8, padding: "0.55rem 0.8rem", marginBottom: 10, fontSize: "0.8rem" }}>
              Aucun numéro secondaire actif.
            </div>
          )}

          {data.pending && (
            <div data-testid="client-wa2-pending" style={{ background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: 8, padding: "0.6rem 0.8rem", marginBottom: 10, fontSize: "0.82rem", color: "#92400E" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                {statusPill("pending")}
              </div>
              Demande pour <strong>{data.pending.requested_number}</strong> — soumise le {formatDate(data.pending.submitted_at)}.
              {data.pending.reason && <div style={{ marginTop: 4, fontStyle: "italic", opacity: 0.85 }}>« {data.pending.reason} »</div>}
            </div>
          )}

          {!data.pending && (
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#475569" }}>
                {data.current_secondary ? "Nouveau numéro WhatsApp secondaire" : "Numéro WhatsApp secondaire"}
                <input
                  data-testid="client-wa2-number"
                  className="form-input"
                  value={number}
                  onChange={e => setNumber(e.target.value)}
                  placeholder="Ex: 50912345678"
                  style={{ width: "100%", marginTop: 4, padding: "0.5rem 0.7rem" }}
                />
              </label>
              <label style={{ fontSize: "0.78rem", fontWeight: 600, color: "#475569" }}>
                Raison (optionnelle)
                <input
                  data-testid="client-wa2-reason"
                  className="form-input"
                  value={reason}
                  onChange={e => setReason(e.target.value)}
                  placeholder="Ex: Mon numéro principal n'est plus sur WhatsApp"
                  style={{ width: "100%", marginTop: 4, padding: "0.5rem 0.7rem" }}
                />
              </label>
              <button
                data-testid="client-wa2-submit"
                type="submit"
                disabled={submitting}
                className="btn"
                style={{ background: "#16A34A", color: "#FFF", padding: "0.55rem", fontWeight: 700 }}
              >{submitting ? "Envoi…" : <><Send size={14} /> Envoyer la demande</>}</button>
            </form>
          )}

          {data.history && data.history.length > 0 && (
            <details style={{ marginTop: 10 }}>
              <summary style={{ cursor: "pointer", fontSize: "0.78rem", color: "#475569", fontWeight: 600 }}>
                Historique ({data.history.length})
              </summary>
              <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
                {data.history.map(h => (
                  <div key={h.id} style={{ fontSize: "0.75rem", color: "#475569", background: "#F8FAFC", padding: "0.4rem 0.6rem", borderRadius: 6 }}>
                    {statusPill(h.status)} <strong>{h.requested_number}</strong> · {formatDate(h.submitted_at)}
                    {h.admin_note && <div style={{ marginTop: 2, opacity: 0.85 }}>Note: {h.admin_note}</div>}
                  </div>
                ))}
              </div>
            </details>
          )}
        </>
      )}
    </div>
  );
}
